import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, input, output, signal, viewChildren, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageCode } from '@phx-erp/shared';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { Textarea, TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { AiFeatureDirective } from '../../../@core/directives/ai-feature.directive';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { UiLocaleService } from '../../../@core/services/i18n/ui-locale.service';
import { NotificationService } from '../../../@core/services/notification/notification.service';
import { getDefaultUiLanguage } from '../../../common/utilities/get-default-ui-language';
import { EntityTranslationRow, TranslateAiError, TranslateAiResult, TranslationFieldValues } from '../../../generated/generated-types';
import { EntityTranslationService } from '../../services/entity-translation.service';
import { TenantService } from '../../services/tenant.service';
import { PhxCheckboxComponent } from '../phx-input-checkbox/phx-input-checkbox.component';
import { PhxSelect } from '../phx-select/phx-select.component';

type PendingEdits = Partial<Record<LanguageCode, Record<string, string>>>;

export interface TranslationEdit {
  languageCode: LanguageCode;
  remove?: boolean;
  [field: string]: string | boolean | LanguageCode;
}

const TARGET_DROPDOWN_THRESHOLD = 7;

const SINGLE_LINE_MAX_CHARS = 36;

/**
 * The backend puts an i18n key in `message` for its own failures and the provider's plain prose in
 * everything else, so the type is what the user gets told.
 */
const AI_ERROR_KEYS: Record<string, string> = {
  REQUEST: 'entity-translations.ai-error.request',
  TIMEOUT: 'entity-translations.ai-error.timeout',
  PROVIDER: 'entity-translations.ai-error.provider',
  CONTENT: 'entity-translations.ai-error.content',
  JSON: 'entity-translations.ai-error.invalid-response',
  VALIDATION: 'entity-translations.ai-error.validation',
  LIMIT: 'entity-translations.ai-error.limit',
  NOT_CONFIGURED: 'entity-translations.ai-error.not-configured'
};

const AI_ERROR_FALLBACK_KEY = 'entity-translations.ai-error.general';

/** `base` is the record's own language - it is the original, so it is never rated as a translation. */
type CoverageState = 'full' | 'partial' | 'none' | 'base';

type FieldState = 'filled' | 'empty' | 'identical' | 'source-empty';

interface LanguageOption {
  code: LanguageCode;
  label: string;
  shortLabel: string;
  coverage: CoverageState;
  coverageLabel: string;
  hasPendingEdits: boolean;
}

/** One field to translate into one language. Carries the stored value so an edit can be recognised. */
interface TranslationJob {
  field: string;
  label: string;
  sourceValue: string;
  storedValue: string;
}

interface TranslationOutcome {
  failedFields: string[];
  errorKey?: string;
}

interface SourceLanguageOption extends LanguageOption {
  hint: string;
  disabled: boolean;
}

interface FieldStateTag {
  icon: string;
  label: string;
  hint: string;
}

interface FieldView {
  field: string;
  label: string;
  startsTall: boolean;
  sourceValue: string;
  targetValue: string;
  editedValue: string;
  isUnsaved: boolean;
  state: FieldState;
  tag: FieldStateTag;
}

/**
 * Translation editor for one record of any entity registered in the backend translation registry.
 * All languages arrive in a single response, so switching source or target is display state only.
 */
@Component({
  selector: 'app-entity-translations-widget',
  templateUrl: './entity-translations-widget.component.html',
  standalone: true,
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    ButtonModule,
    TextareaModule,
    PhxCheckboxComponent,
    PhxSelect,
    SelectButtonModule,
    TagModule,
    TooltipModule,
    AiFeatureDirective
  ]
})
export class EntityTranslationsWidgetComponent {

  entity = input.required<string>();

  recordId = input<string>(null);

  translationsChange = output<TranslationEdit[]>();

  languages = computed<LanguageCode[]>(() => this.tenantService.currentTenantSig()?.availableContentLanguages ?? []);

  // The system's fallback language, from `phoenix-ui-config.json`. 
  private readonly baseLanguage = getDefaultUiLanguage();

  targetLanguages = computed<LanguageCode[]>(() => this.languages().filter(code => code !== this.baseLanguage));

  sourceLanguages = computed<LanguageCode[]>(() => [this.baseLanguage, ...this.targetLanguages()]);

  loading = signal<boolean>(false);

  row = signal<EntityTranslationRow>(null);

  edits = signal<PendingEdits>({});

  targetLanguage = signal<LanguageCode>(null);

  sourceLanguage = signal<LanguageCode>(null);

  translatingFields = signal<string[]>([]);

  isTranslatingAll = signal<boolean>(false);

  isTranslatingAllLanguages = signal<boolean>(false);

  /** Lets the AI buttons replace values that are already there, instead of only filling the gaps. */
  overwriteExisting = signal<boolean>(false);

  /** Languages the user asked to delete on save. Kept apart from `edits` - it is not a field change. */
  removals = signal<LanguageCode[]>([]);

  private readonly entityTranslationService = inject(EntityTranslationService);

  private readonly notificationService = inject(NotificationService);

  private readonly uiLocaleService = inject(UiLocaleService);

  private readonly tenantService = inject(TenantService);

  private readonly i18n = inject(I18nService);

  private readonly host = inject(ElementRef);

  private readonly destroyRef = inject(DestroyRef);

  private readonly valueFields = viewChildren(Textarea);

  constructor() {
    effect(() => void this.load(this.recordId()));
    effect(() => this.ensureLanguageSelection());
    afterNextRender(() => this.remeasureWhenShown());
  }

  /** An opened tab stays in the DOM, and autoResize measures 0 while it is hidden - so re-measure. */
  private remeasureWhenShown(): void {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.valueFields().forEach(field => field.resize());
      }
    });
    observer.observe(this.host.nativeElement as Element);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  /** The tenant's languages arrive asynchronously, so the pickers settle once they are known. */
  private ensureLanguageSelection(): void {
    const languages = this.languages();
    if (!languages.length) {
      return;
    }
    if (!this.sourceLanguages().includes(this.sourceLanguage())) {
      this.sourceLanguage.set(this.defaultSourceLanguage());
    }
    const targets = this.targetLanguages();
    if (!targets.includes(this.targetLanguage())) {
      this.targetLanguage.set(targets.find(code => code !== this.sourceLanguage()) ?? targets[0] ?? null);
    }
  }

  languageOptions = computed<LanguageOption[]>(() => this.optionsFor(this.targetLanguages()));

  useTargetDropdown = computed<boolean>(() => this.targetLanguages().length >= TARGET_DROPDOWN_THRESHOLD);

  /** The base language is a source but never a target - the record itself is the record in it. */
  sourceOptions = computed<SourceLanguageOption[]>(() => {
    const target = this.targetLanguage();
    return this.optionsFor(this.sourceLanguages()).map(option => ({
      ...option,
      hint: option.code === target ? this.i18n.translate('entity-translations.is-target-language') : option.coverageLabel,
      disabled: option.code === target || option.coverage === 'none'
    }));
  });

  private optionsFor(codes: LanguageCode[]): LanguageOption[] {
    return codes.map(code => {
      const coverage = this.coverageOf(code);
      const labelKey = this.coverageLabelKey(coverage);
      return {
        code,
        label: this.i18n.translate(`enums.languageCodes.${code}`),
        shortLabel: code.slice(0, 2).toUpperCase(),
        coverage,
        coverageLabel: labelKey ? this.i18n.translate(labelKey) : '',
        hasPendingEdits: !!Object.keys(this.edits()[code] ?? {}).length || this.removals().includes(code)
      };
    });
  }

  sourceTag = computed<string>(() => this.sourceLanguage()?.slice(0, 2).toUpperCase() ?? '');

  targetTag = computed<string>(() => this.targetLanguage()?.slice(0, 2).toUpperCase() ?? '');

  fieldViews = computed<FieldView[]>(() => {
    const source = this.sourceLanguage();
    const target = this.targetLanguage();
    return (this.row()?.fields ?? []).map(field => {
      const sourceValue = this.valueOf(field, source);
      const targetValue = this.valueOf(field, target);
      const editedValue = this.editedValueOf(field, target);
      const state = this.stateOf(sourceValue, targetValue);
      return {
        field: field.field,
        label: field.label,
        startsTall: Math.max(sourceValue.length, targetValue.length) >= SINGLE_LINE_MAX_CHARS,
        sourceValue,
        targetValue,
        editedValue,
        isUnsaved: editedValue !== targetValue,
        state,
        tag: this.tagOf(state)
      };
    });
  });

  translatedFieldCount = computed<number>(() => this.fieldViews().filter(view => view.state !== 'empty').length);

  isTranslating = computed<boolean>(() =>
    this.isTranslatingAll() || this.isTranslatingAllLanguages() || !!this.translatingFields().length);

  canTranslateAll = computed<boolean>(() =>
    !!this.jobsFor(this.targetLanguage()).length && this.sourceLanguage() !== this.targetLanguage()
    && !this.isMarkedForRemoval() && !this.isTranslating());

  canTranslateAllLanguages = computed<boolean>(() =>
    !!this.languagesToFill().length && !this.isTranslating());

  isMarkedForRemoval = computed<boolean>(() => this.removals().includes(this.targetLanguage()));

  /** Only a stored language can be deleted - an empty one has nothing to remove. */
  canRemoveLanguage = computed<boolean>(() =>
    (this.row()?.existingLanguages ?? []).includes(this.targetLanguage()) && !this.isTranslating());

  /** Marking drops the language's pending edits - they would be written and deleted in one save. */
  onToggleRemoveLanguage(): void {
    const language = this.targetLanguage();
    const isMarked = this.removals().includes(language);
    this.removals.update(current => isMarked ? current.filter(code => code !== language) : [...current, language]);
    if (!isMarked) {
      this.edits.update(current => {
        const next = { ...current };
        delete next[language];
        return next;
      });
    }
    this.translationsChange.emit(this.toTranslationEdits());
  }

  isFieldTranslating(field: string): boolean {
    return this.translatingFields().includes(field);
  }

  async onTranslateField(view: FieldView): Promise<void> {
    await this.runForTarget([{ field: view.field, label: view.label, sourceValue: view.sourceValue, storedValue: view.targetValue }], false);
  }

  async onTranslateAll(): Promise<void> {
    await this.runForTarget(this.jobsFor(this.targetLanguage()), true);
  }

  /**
   * One request per language, in parallel - DeepL takes a single target language per call, so a
   * combined request would only move the same loop somewhere else.
   */
  async onTranslateAllLanguages(): Promise<void> {
    const languages = this.languagesToFill();
    this.isTranslatingAllLanguages.set(true);
    try {
      const outcomes = await Promise.all(languages.map(code => this.translateInto(code, this.jobsFor(code))));
      this.notifyAllLanguages(languages.length, outcomes);
    } finally {
      this.isTranslatingAllLanguages.set(false);
    }
  }

  private async runForTarget(jobs: TranslationJob[], isAll: boolean): Promise<void> {
    this.translatingFields.set(jobs.map(job => job.field));
    this.isTranslatingAll.set(isAll);
    try {
      const outcome = await this.translateInto(this.targetLanguage(), jobs);
      if (outcome.errorKey) {
        this.notificationService.error(outcome.errorKey);
      } else if (outcome.failedFields.length) {
        this.notificationService.warning('entity-translations.ai-error.fields-failed', { fields: outcome.failedFields.join(', ') });
      }
    } finally {
      this.translatingFields.set([]);
      this.isTranslatingAll.set(false);
    }
  }

  /** Suggestions land as pending edits, nothing reaches the database until the user saves. */
  private async translateInto(language: LanguageCode, jobs: TranslationJob[]): Promise<TranslationOutcome> {
    let result: TranslateAiResult;
    try {
      result = await this.entityTranslationService.translateTexts({
        sourceLanguage: this.sourceLanguage(),
        targetLanguage: language,
        context: this.aiContext(),
        items: jobs.map(job => ({ key: job.field, text: job.sourceValue }))
      });
    } catch (error: any) {
      // only transport failures land here - the backend's own failures arrive in result.errors
      console.error('AI translation request failed', error);
      return { failedFields: this.labelsOf(jobs), errorKey: AI_ERROR_FALLBACK_KEY };
    }
    const requestError = result?.errors?.[0];
    if (requestError) {
      return { failedFields: this.labelsOf(jobs), errorKey: this.aiErrorKey(requestError) };
    }
    const failedFields: string[] = [];
    for (const job of jobs) {
      const text = result?.items?.find(item => item.key === job.field)?.text;
      if (text) {
        this.applyEdit(language, job.field, job.storedValue, text);
      } else {
        failedFields.push(this.i18n.translate(job.label));
      }
    }
    return { failedFields };
  }

  private notifyAllLanguages(requested: number, outcomes: TranslationOutcome[]): void {
    const failed = outcomes.filter(outcome => outcome.errorKey || outcome.failedFields.length).length;
    if (failed === requested) {
      this.notificationService.error(outcomes.find(outcome => outcome.errorKey)?.errorKey ?? AI_ERROR_FALLBACK_KEY);
      return;
    }
    if (failed) {
      this.notificationService.warning('entity-translations.ai-all-languages-partial', { done: requested - failed, total: requested });
      return;
    }
    this.notificationService.info('entity-translations.ai-all-languages-done', { count: requested });
  }

  private languagesToFill(): LanguageCode[] {
    return this.targetLanguages().filter(code =>
      code !== this.sourceLanguage() && !this.removals().includes(code) && !!this.jobsFor(code).length);
  }

  /** Filling the gaps unless asked to overwrite: a filled field is retranslated through its own button. */
  private jobsFor(language: LanguageCode): TranslationJob[] {
    const source = this.sourceLanguage();
    return (this.row()?.fields ?? [])
      .filter(field => !!this.valueOf(field, source)
        && (this.overwriteExisting() || !this.editedValueOf(field, language)))
      .map(field => ({
        field: field.field,
        label: field.label,
        sourceValue: this.valueOf(field, source),
        storedValue: this.valueOf(field, language)
      }));
  }

  private labelsOf(jobs: TranslationJob[]): string[] {
    return jobs.map(job => this.i18n.translate(job.label));
  }

  private aiErrorKey(error: TranslateAiError): string {
    return AI_ERROR_KEYS[error?.type] ?? AI_ERROR_FALLBACK_KEY;
  }

  /** Short strings like a unit's "Stk" are ambiguous without knowing what kind of record they sit on. */
  private aiContext(): string {
    return this.i18n.translate('entity-translations.ai-context', { entity: this.i18n.translate(`entities.${this.entity()}._`) });
  }

  coverageDotClass(coverage: CoverageState): string {
    if (coverage === 'full') {
      return 'bg-green-500';
    }
    return coverage === 'partial' ? 'bg-amber-500' : 'border border-gray-400 dark:border-gray-500';
  }

  onCopyFromSource(view: FieldView): void {
    this.onValueChange(view, view.sourceValue);
  }

  onValueChange(view: FieldView, value: string): void {
    this.applyEdit(this.targetLanguage(), view.field, view.targetValue, value);
  }

  /** An edit that matches the stored value is not an edit - the save button must go quiet again. */
  private applyEdit(language: LanguageCode, field: string, storedValue: string, value: string): void {
    this.edits.update(current => {
      const forLanguage = { ...(current[language] ?? {}) };
      if (value === storedValue) {
        delete forLanguage[field];
      } else {
        forLanguage[field] = value;
      }
      const next = { ...current, [language]: forLanguage };
      if (!Object.keys(forLanguage).length) {
        delete next[language];
      }
      return next;
    });
    this.translationsChange.emit(this.toTranslationEdits());
  }

  /**
   * One entry per touched language. A language whose fields all end up empty is removed rather than
   * stored blank, otherwise the record would keep reporting itself as translated into it.
   */
  private toTranslationEdits(): TranslationEdit[] {
    const fields = this.row()?.fields ?? [];
    const existingLanguages = this.row()?.existingLanguages ?? [];
    const edits: TranslationEdit[] = this.removals()
      .filter(languageCode => existingLanguages.includes(languageCode))
      .map(languageCode => ({ languageCode, remove: true }));
    for (const [code, edited] of Object.entries(this.edits())) {
      const languageCode = code as LanguageCode;
      const merged: TranslationEdit = { languageCode };
      for (const field of fields) {
        merged[field.field] = (edited[field.field] ?? this.valueOf(field, languageCode)) || null;
      }
      if (fields.some(field => !!merged[field.field])) {
        edits.push(merged);
      } else if (existingLanguages.includes(languageCode)) {
        edits.push({ languageCode, remove: true });
      }
    }
    return edits;
  }

  async reload(): Promise<void> {
    this.edits.set({});
    this.removals.set([]);
    await this.load(this.recordId());
  }

  onTargetLanguageChange(code: LanguageCode): void {
    if (!code) {
      return;
    }
    this.targetLanguage.set(code);
    if (this.sourceLanguage() !== code) {
      return;
    }
    this.sourceLanguage.set(this.languages().find(other => other !== code && this.coverageOf(other) !== 'none') ?? null);
  }

  private async load(recordId: string): Promise<void> {
    if (!recordId) {
      this.row.set(null);
      return;
    }
    this.loading.set(true);
    try {
      this.row.set(await this.entityTranslationService.getRecordTranslations(this.entity(), recordId, this.languages()));
    } finally {
      this.loading.set(false);
    }
  }

  private defaultSourceLanguage(): LanguageCode {
    const contentLanguage = this.uiLocaleService.contentLanguage;
    return this.sourceLanguages().includes(contentLanguage) ? contentLanguage : this.baseLanguage;
  }

  /** Stored value, never the pending edit - state cues must describe what the database holds. */
  private valueOf(field: TranslationFieldValues, code: LanguageCode): string {
    return field.values.find(value => value.languageCode === code)?.value ?? '';
  }

  private editedValueOf(field: TranslationFieldValues, code: LanguageCode): string {
    return this.edits()[code]?.[field.field] ?? this.valueOf(field, code);
  }

  /**
   * Measured against the base language rather than the field list: a field the record leaves empty
   * in its own language (a product's "Zusatz" lines, say) has nothing to translate, so it must not
   * count as a gap in any other language.
   */
  private coverageOf(code: LanguageCode): CoverageState {
    if (code === this.baseLanguage) {
      return 'base';
    }
    const fields = this.row()?.fields ?? [];
    if (!fields.some(field => !!this.valueOf(field, code))) {
      return 'none';
    }
    const hasGap = fields.some(field => !!this.valueOf(field, this.baseLanguage) && !this.valueOf(field, code));
    return hasGap ? 'partial' : 'full';
  }

  private coverageLabelKey(coverage: CoverageState): string {
    switch (coverage) {
      case 'base':
        return null;
      case 'none':
        return 'entity-translations.no-values';
      case 'full':
        return 'entity-translations.complete';
      default:
        return 'entity-translations.partly-translated';
    }
  }

  private tagOf(state: FieldState): FieldStateTag {
    if (state === 'identical') {
      return { icon: 'fa-solid fa-equals', label: this.i18n.translate('entity-translations.identical'), hint: this.i18n.translate('entity-translations.identical-hint') };
    }
    return null;
  }

  private stateOf(sourceValue: string, targetValue: string): FieldState {
    if (!targetValue) {
      return 'empty';
    }
    if (!sourceValue) {
      return 'source-empty';
    }
    return sourceValue.trim() === targetValue.trim() ? 'identical' : 'filled';
  }
}
